import dayjs from 'dayjs'

export const setToStartOrEnd = (date, isStart) => {
  const year = date.year()
  const month = date.month()
  const day = date.date()
  const hours = isStart ? 0 : 23
  const minutes = isStart ? 0 : 59
  const seconds = 0
  const combinedUTC = dayjs.utc({
    year,
    month,
    day,
    hour: hours,
    minute: minutes,
    second: seconds,
  })
  return combinedUTC
}

export const onDateChange = (
  dates,
  startTimeFilter,
  endTimeFilter,
  updateStart,
  updateEnd,
  toggleCalendar,
) => {
  if (
    dates[0] &&
    dates[1] &&
    dates[0] !== startTimeFilter &&
    dates[1] !== endTimeFilter
  ) {
    updateStart(setToStartOrEnd(dayjs(dates[0]), true))
    updateEnd(setToStartOrEnd(dayjs(dates[1]), false))
    toggleCalendar(false)
  }
}

export const getTimeAgoText = (lastUpdatedTime) => {
  const now = new Date()
  const diffMs = now - lastUpdatedTime
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
  return `${diffHours} hr${diffHours === 1 ? '' : 's'} ago`
}

export const getReportDay = (fault) =>
  new Date(fault.created_at).toLocaleString('en-US', { dateStyle: 'long' })
export const getReportHour = (fault) =>
  new Date(fault.created_at).toLocaleString('en-US', {
    hour: 'numeric',
    hour12: true,
  })

export function groupFaults(faults) {
  const groupedFaults = faults.reduce((grouped, fault) => {
    const day = getReportDay(fault)
    const hour = getReportHour(fault)

    grouped[day] = grouped[day] || {}
    grouped[day][hour] = grouped[day][hour] || {
      day,
      hour,
      component: [],
      device: [],
      other: [],
      harness: [],
    }
    grouped[day][hour][fault.type].push(fault)
    return grouped
  }, {})

  return groupedFaults
}

export const FILTER_TIME_WINDOW = [
  { label: '1d', value: '1d' },
  { label: '1w', value: '1w' },
  { label: 'Custom', value: 'custom' },
]

/**
 * Merges usage events that share the same session_id into a single event,
 * summing the time spent and using the latest loggedAt timestamp.
 *
 * @param {Array} data - Array of usage events
 * @param {string} timeSpentEventType - The event type representing time-spent intervals
 * @param {string} timeKey - The key in eventData holding the time value
 */
export const mergeIntervalsBySessionId = (
  data,
  timeSpentEventType,
  timeKey,
) => {
  const intervals = data.filter(
    (event) => event.eventType === timeSpentEventType,
  )
  const otherEvents = data.filter(
    (event) => event.eventType !== timeSpentEventType,
  )

  const intervalsById = {}
  intervals.forEach((interval) => {
    const id = interval.eventData?.session_id || 'no_session_id'
    if (intervalsById[id]) {
      intervalsById[id].push(interval)
    } else {
      intervalsById[id] = [interval]
    }
  })

  const mergedIntervals = []
  Object.keys(intervalsById).forEach((id) => {
    if (id === 'no_session_id') {
      mergedIntervals.push(...intervalsById[id])
      return
    }
    const sessionIntervals = intervalsById[id]
    if (sessionIntervals.length === 1) {
      mergedIntervals.push(sessionIntervals[0])
    } else {
      let totalTimeSpent = 0
      let latestLoggedAt = -1
      sessionIntervals.forEach((interval) => {
        totalTimeSpent += interval.eventData?.[timeKey] || 0
        if (interval.loggedAt && interval.loggedAt > latestLoggedAt) {
          latestLoggedAt = interval.loggedAt
        }
      })
      mergedIntervals.push({
        ...sessionIntervals[0],
        eventData: {
          ...sessionIntervals[0].eventData,
          [timeKey]: totalTimeSpent,
        },
        loggedAt: latestLoggedAt,
      })
    }
  })

  return mergedIntervals.concat(otherEvents)
}

/**
 * Returns the user-facing identifier value from a raw usage record.
 * Maps the config value ('user_id' or 'user_email') to the camelCase record field.
 */
export function getUserIdentifierFromRecord(
  record,
  userIdentifier = 'user_id',
) {
  if (!record) return '-'
  if (userIdentifier === 'user_email') {
    return record.userEmail || '-'
  }
  return record.userId || '-'
}

/**
 * Safely parses the eventData field of a usage record (may be a JSON string or object).
 */
export function getEventData(record) {
  let eventData = record?.eventData
  try {
    if (typeof eventData === 'string') {
      eventData = JSON.parse(eventData)
    }
  } catch {
    return {}
  }
  return eventData || {}
}

/**
 * Formats a millisecond duration to a human-readable string (e.g. '1h 23m 45s').
 */
export function formatMs(ms) {
  if (!ms || ms < 1000) return ms ? `${ms / 1000}s` : '-'
  const seconds = Math.floor(ms / 1000) % 60
  const minutes = Math.floor(ms / (1000 * 60)) % 60
  const hours = Math.floor(ms / (1000 * 60 * 60)) % 24
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))
  const parts = []
  if (days) parts.push(`${days}d`)
  if (hours) parts.push(`${hours}h`)
  if (minutes) parts.push(`${minutes}m`)
  if (seconds) parts.push(`${seconds}s`)
  return parts.length ? parts.join(' ') : '0s'
}

/**
 * Formats a timestamp (ms number or ISO string) to 'YYYY-MM-DD HH:mm:ss'.
 */
export function formatTs(ts) {
  if (!ts) return '-'
  if (typeof ts === 'number') return dayjs(ts).format('YYYY-MM-DD HH:mm:ss')
  const parsed = dayjs(ts)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : String(ts)
}

/**
 * Converts a timestamp (ms number or ISO string) to a numeric ms value.
 */
export function toMs(ts) {
  if (!ts) return 0
  if (typeof ts === 'number') return ts
  const parsed = dayjs(ts)
  return parsed.isValid() ? parsed.valueOf() : 0
}

/**
 * Filters usage events by a selectedDate string in 'MM/DD' format.
 * Accounts for year rollover (dates in the future are assumed to be the previous year).
 */
export function filterUsageEventsByDate(events, selectedDate) {
  if (!selectedDate) return events
  const now = dayjs()
  let selYear = now.year()
  let selFull = dayjs(`${selYear}/${selectedDate}`, 'YYYY/MM/DD')
  if (selFull.isAfter(now, 'day')) {
    selYear = selYear - 1
    selFull = dayjs(`${selYear}/${selectedDate}`, 'YYYY/MM/DD')
  }
  return events.filter((row) => {
    const loggedDay = dayjs(row.loggedAt)
    if (!loggedDay || !loggedDay.isValid()) return false
    return loggedDay.isSame(selFull, 'day')
  })
}

/**
 * Derives sorted user option objects from sortedData, for use in filter dropdowns.
 */
export function buildUserOptions(sortedData, eventType, userIdentifier) {
  const users = new Set()
  const eventTypes = Array.isArray(eventType) ? eventType : [eventType]
  eventTypes.forEach((type) => {
    ;(sortedData[type] || []).forEach((d) => {
      const user = getUserIdentifierFromRecord(d, userIdentifier)
      if (user && user !== '-') users.add(user)
    })
  })
  return [...users].sort().map((u) => ({ label: u, value: u }))
}

/**
 * Derives sorted date option objects (MM/DD) from sortedData, for use in filter dropdowns.
 */
export function buildDateOptions(sortedData, eventType) {
  const dates = new Set()
  ;(sortedData[eventType] || []).forEach((d) => {
    if (d.loggedAt) dates.add(dayjs(d.loggedAt).format('MM/DD'))
  })
  return [...dates].sort().map((d) => ({ label: d, value: d }))
}

/**
 * Derives sorted plant option objects from sortedData, for use in filter dropdowns.
 */
export function buildPlantOptions(sortedData, eventType) {
  const plants = new Set()
  ;(sortedData[eventType] || []).forEach((d) => {
    const plant = getEventData(d).plant
    if (plant) plants.add(plant)
  })
  return [...plants].sort().map((p) => ({ label: p, value: p }))
}
